import * as React from 'react';
import { format, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

function parseDateOnly(s: string): Date | undefined {
  if (!s) return undefined;
  const d = parse(s, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
}

interface DatePickerProps {
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'YYYY-MM-DD',
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(value);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const minDate = min ? parseDateOnly(min) : undefined;
  const maxDate = max ? parseDateOnly(max) : undefined;

  const handleInput = (v: string) => {
    setInputValue(v);
    if (!v) {
      onChange('');
      return;
    }
    if (parseDateOnly(v)) onChange(v);
  };

  const handleBlur = () => {
    if (!inputValue) return;
    const parsed = parseDateOnly(inputValue);
    if (!parsed) {
      setInputValue('');
      onChange('');
      return;
    }
    if (maxDate && parsed > maxDate) {
      const clamped = format(maxDate, 'yyyy-MM-dd');
      setInputValue(clamped);
      onChange(clamped);
      return;
    }
    if (minDate && parsed < minDate) {
      const clamped = format(minDate, 'yyyy-MM-dd');
      setInputValue(clamped);
      onChange(clamped);
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      const str = format(date, 'yyyy-MM-dd');
      setInputValue(str);
      onChange(str);
    }
    setOpen(false);
  };

  const selected = parseDateOnly(inputValue);
  const isOutOfRange =
    minDate || maxDate
      ? (d: Date) => (!!maxDate && d > maxDate) || (!!minDate && d < minDate)
      : undefined;

  return (
    <div className={cn('relative', className)}>
      <Input
        className="h-8 w-full pr-8 font-mono text-sm"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => handleInput(e.target.value)}
        onBlur={handleBlur}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="选择日期"
            className="absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CalendarIcon className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="end">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected ?? new Date()}
            disabled={isOutOfRange}
            onSelect={handleCalendarSelect}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
